import { useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { VESSEL_DATA_MOCK_DASHBOARDS, VESSEL_DATA_MOCK_VESSELS } from "../mocks/vessel-data.mocks";
import type { VesselDataState } from "../models/vessel-data.models";
import {
  getVesselDataDashboard,
  getVesselDataErrorMessage,
  getVesselDataVessels,
} from "../services/vessel-data.service";
import { vesselDataMockEnabledAtom } from "./vessel-data.atoms";

const initialState: VesselDataState = {
  vessels: [],
  selectedImoNumber: null,
  dashboard: null,
  isLoadingVessels: true,
  isLoadingDashboard: false,
  error: null,
};

// Owns request cancellation and source selection so the page only renders prepared dashboard state.
export const useVesselDataDashboard = () => {
  const mockEnabled = useAtomValue(vesselDataMockEnabledAtom);
  const [query, setQuery] = useState("");
  const [refreshSequence, setRefreshSequence] = useState(0);
  const [state, setState] = useState<VesselDataState>(initialState);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // Clears the previous source and selection before the debounced request can expose a new result set.
    setState((current) => ({
      ...current,
      vessels: [],
      selectedImoNumber: null,
      dashboard: null,
      isLoadingVessels: true,
      isLoadingDashboard: false,
      error: null,
    }));

    const timer = window.setTimeout(async () => {
      try {
        const normalizedQuery = query.trim().toLowerCase();
        const response = mockEnabled
          ? { vessels: VESSEL_DATA_MOCK_VESSELS.filter((vessel) => !normalizedQuery || `${vessel.vesselName} ${vessel.imoNumber} ${vessel.vesselType ?? ""}`.toLowerCase().includes(normalizedQuery)) }
          : await getVesselDataVessels(query, controller.signal);

        if (active) {
          setState((current) => {
            const selectedStillVisible = response.vessels.some((vessel) => vessel.imoNumber === current.selectedImoNumber);

            return {
              ...current,
              vessels: response.vessels,
              selectedImoNumber: selectedStillVisible ? current.selectedImoNumber : response.vessels[0]?.imoNumber ?? null,
              dashboard: selectedStillVisible ? current.dashboard : null,
              isLoadingVessels: false,
            };
          });
        }
      } catch (error) {
        if (active && !controller.signal.aborted) {
          setState((current) => ({ ...current, vessels: [], isLoadingVessels: false, error: getVesselDataErrorMessage(error) }));
        }
      }
    }, 250);

    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [mockEnabled, query]);

  useEffect(() => {
    if (!state.selectedImoNumber) {
      setState((current) => ({ ...current, dashboard: null, isLoadingDashboard: false }));

      return;
    }

    const controller = new AbortController();
    let active = true;
    setState((current) => ({ ...current, dashboard: null, isLoadingDashboard: true, error: null }));

    const loadDashboard = async () => {
      try {
        const dashboard = mockEnabled
          ? VESSEL_DATA_MOCK_DASHBOARDS[state.selectedImoNumber!]
          : await getVesselDataDashboard(state.selectedImoNumber!, controller.signal);

        if (!dashboard) {
          throw new Error("No modeled mock dashboard exists for the selected vessel.");
        }

        if (active) {
          setState((current) => ({ ...current, dashboard, isLoadingDashboard: false }));
        }
      } catch (error) {
        if (active && !controller.signal.aborted) {
          setState((current) => ({ ...current, dashboard: null, isLoadingDashboard: false, error: getVesselDataErrorMessage(error) }));
        }
      }
    };

    void loadDashboard();

    return () => {
      active = false;
      controller.abort();
    };
  }, [mockEnabled, refreshSequence, state.selectedImoNumber]);

  const selectVessel = useCallback((imoNumber: string) => {
    setState((current) => ({
      ...current,
      selectedImoNumber: imoNumber,
      dashboard: null,
      isLoadingDashboard: Boolean(imoNumber),
      error: null,
    }));
  }, []);
  const refresh = useCallback(() => setRefreshSequence((current) => current + 1), []);

  return { ...state, mockEnabled, query, setQuery, selectVessel, refresh };
};
